import { Injectable } from '@angular/core';

/**
 * Masks pattern-shaped sensitive data out of the bug-report payload before it
 * leaves the browser. This is the first, regex-based pass of the redaction
 * strategy described in the bug-tool build plan (§M1.2a). It catches the
 * "pattern-shaped identifiers" class — email, phone, SSN, card numbers and
 * `Bearer` tokens — in both free strings and (recursively) in object/array
 * structures.
 *
 * It deliberately does NOT attempt to catch free-text PII (owner names,
 * property addresses, dollar balances) — regex can't do that reliably, so the
 * plan keeps that data server-side in `data_snapshot` instead. The fail-closed
 * re-scan that blocks submit is also deferred per the build plan; this service
 * is the masking primitive everything else builds on.
 */
@Injectable({ providedIn: 'root' })
export class RedactionService {
    /** Replacement token written in place of any matched sensitive value. */
    public static readonly MASK = '[REDACTED]';

    /**
     * Ordered list of pattern matchers. Bearer tokens are matched first so the
     * token body isn't partially consumed by a narrower pattern.
     */
    private readonly _patterns: ReadonlyArray<RegExp> = [
        // `Bearer <token>` (JWTs, opaque API tokens). Case-insensitive scheme.
        /Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi,
        // Email addresses.
        /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g,
        // Credit-card-shaped runs of 13-16 digits, optionally separated by
        // spaces or dashes (matched before phone so it wins on long runs).
        /\b(?:\d[ -]*?){13,16}\b/g,
        // US SSN (###-##-####).
        /\b\d{3}-\d{2}-\d{4}\b/g,
        // Phone numbers — US-style with optional country code, separators and
        // parens. Requires at least 10 digits' worth of structure.
        /(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/g,
    ];

    /** Mask every known sensitive pattern inside a single string. */
    public redactString(value: string): string {
        let result = value;
        for (const pattern of this._patterns) {
            result = result.replace(pattern, RedactionService.MASK);
        }
        return result;
    }

    /**
     * Deep-redact any value: strings are pattern-masked, arrays/objects are
     * walked recursively, and primitives pass through untouched. Returns a new
     * structure — the input is never mutated.
     */
    public redact<T>(value: T): T {
        return this._redactValue(value) as T;
    }

    private _redactValue(value: unknown): unknown {
        if (typeof value === 'string') {
            return this.redactString(value);
        }
        if (Array.isArray(value)) {
            return value.map((item) => this._redactValue(item));
        }
        if (value !== null && typeof value === 'object') {
            const out: Record<string, unknown> = {};
            for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
                out[key] = this._redactValue(val);
            }
            return out;
        }
        return value;
    }
}
